import { useMutation, useQueryClient } from '@tanstack/react-query';

import { fetchClient } from '@/lib/openapi-fetch/fetchClient';
import { availabilityPreferencesQueryKeys } from '@/modules/availability/queries/queryKeys';
import { endpoints } from '@/modules/users/constants';
import type { components } from '@/types/apiSchema';

type AvailabilityPreferencesRequest =
  components['schemas']['AvailabilityPreferencesRequest'];

interface SaveAvailabilityPreferencesVariables {
  body: AvailabilityPreferencesRequest;
  userId: string;
}

export const useSaveAvailabilityPreferencesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      body,
      userId,
    }: SaveAvailabilityPreferencesVariables) => {
      const { data } = await fetchClient.PUT(
        endpoints.availabilityPreferences,
        {
          params: { path: { id: userId } },
          body,
        },
      );

      return data;
    },
    onSuccess: (preferences, { userId }) => {
      // Seed the cache from the PUT response: the invalidated GET is still in
      // flight, and a second edit made in the meantime would otherwise be
      // built on the stale preferences and silently revert the first one.
      if (preferences) {
        queryClient.setQueryData(
          availabilityPreferencesQueryKeys.detail(userId),
          preferences,
        );
      }

      void queryClient.invalidateQueries({
        queryKey: availabilityPreferencesQueryKeys.detail(userId),
      });
    },
  });
};
